import { useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Ticket, Trophy } from 'lucide-react';
import { mlDecimal } from './CfbTicketBuilder';
import { useBetMode } from '../lib/betMode';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/sports-betting`;
const pct = (p) => `${Math.round(p * 100)}%`;
const SIZES = [4, 6, 8];

// Straight win/loss NFL ticket (moneyline only, no spreads) from the next game day's games, safest first.
export default function NflWinTicket({ wins, slate }) {
  const [pick, setSize] = useState(6);
  const [stake, setStake] = useState(10);
  const mode = useBetMode();
  const open = useMemo(() => (wins || []).filter((w) => new Date(w.date) > new Date() && w.ml && w.ml !== 'OFF')
    .sort((a, b) => b.prob - a.prob), [wins]);
  const sizes = [...new Set(SIZES.filter((n) => n < open.length).concat(Math.min(open.length, 8)))];
  const size = Math.min(pick, open.length);
  const legs = open.slice(0, size);
  if (!open.length) return null;
  const chance = legs.reduce((a, w) => a * w.prob, 1);
  const payout = stake * legs.reduce((a, w) => a * (mlDecimal(w.ml) || 1), 1);
  const track = async () => {
    const body = {
      leg_count: legs.length, stake, potential_payout: Math.round(payout * 100) / 100,
      descriptor: `NFL ${slate?.label || ''} win/loss ${legs.length}-leg`,
      legs: legs.map((w, i) => ({ id: `w${i}`, league: 'nfl', bet_type: 'moneyline', selection: `${w.team} moneyline`, odds: String(w.ml), game_date: w.date })),
    };
    try { await axios.post(`${API}/parlays`, body); toast.success('Win/loss ticket added to your tracker'); } catch (e) { toast.error(`Could not add: ${e.response?.data?.detail || e.message}`); }
  };
  return (
    <div className="rounded-xl border border-emerald-500/40 bg-emerald-950/20 p-3 space-y-2" data-testid="nfl-win-ticket">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-sm font-bold text-emerald-300 flex items-center gap-1.5"><Trophy className="w-4 h-4" />NFL {slate?.label || ''} · win/loss ticket</p>
          <p className="text-[11px] text-zinc-400">Straight moneyline, no spreads · safest winners first ({open.length} game{open.length === 1 ? '' : 's'} left)</p>
        </div>
        <button type="button" onClick={track} data-testid="nfl-win-track"
          className="shrink-0 inline-flex items-center gap-1 rounded-lg bg-amber-500 px-3 py-1.5 text-xs font-bold text-black hover:bg-amber-400 transition-colors">
          <Ticket className="w-3.5 h-3.5" />Track
        </button>
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        {sizes.map((n) => (
          <button key={n} type="button" onClick={() => setSize(n)} data-testid={`nfl-win-size-${n}`}
            className={`rounded-full border px-2.5 py-0.5 text-[11px] ${size === n ? 'bg-emerald-500 text-black border-emerald-500 font-semibold' : 'border-zinc-700 text-zinc-400'}`}>{n} leg{n === 1 ? '' : 's'}</button>
        ))}
        <label className="ml-auto flex items-center gap-1 text-[11px] text-zinc-400">Stake $
          <input type="number" min="1" value={stake} onChange={(e) => setStake(Math.max(1, Number(e.target.value) || 1))}
            className="w-14 rounded bg-zinc-900 border border-zinc-700 px-1.5 py-0.5 text-white" data-testid="nfl-win-stake" />
        </label>
      </div>
      <ol className="space-y-1">
        {legs.map((w, i) => (
          <li key={w.event_id} className="flex items-center gap-2 text-[12px]" data-testid={`nfl-win-leg-${i}`}>
            {w.team_logo && <img src={w.team_logo} alt="" className="w-5 h-5 object-contain" />}
            <span className="flex-1 min-w-0 truncate">{mode === 'pro'
              ? <><b className="text-white">{w.team} ML {w.ml}</b> <span className="text-zinc-500">· {w.game}</span></>
              : <><b className="text-white">The {w.team} win</b> <span className="text-zinc-500">· {w.game}</span></>}</span>
            <span className="font-mono font-bold text-emerald-300">{pct(w.prob)}</span>
          </li>
        ))}
      </ol>
      <p className="text-[11px] text-zinc-300" data-testid="nfl-win-summary">
        All {legs.length} win: <b className="text-white">{pct(chance)}</b> · ${stake} pays about <b className="text-emerald-300">${Math.round(payout).toLocaleString()}</b>
      </p>
    </div>
  );
}
